<template>
  <div class="row my-4">
    <div class="col-12">
      <section-title
        class="mt-4 mb-3"
        has-line
        title="Identificador de Proveedor"
      />
    </div>

    <div class="row col-12 mb-3 align-items-stretch">
      <div class="col-md-4">
        <span class="font-weight-normal text-gray font-14">
          Número de Identificador Nacional:
        </span>

        <p v-if="!isEditing && nationalIdentifier" class="font-16 mb-0 mt-1">
          <strong>{{ nationalIdentifier }}</strong>
        </p>

        <div
          v-else
          :class="{
            'medicaid-eligibility-field-invalid': !!nationalIdentifierError,
          }"
        >
          <custom-input
            v-model="nationalIdentifierDraft"
            class="mt-1"
            maxlength="50"
            placeholder="Ingrese el número de identificador nacional"
            @input="onNationalIdentifierInput"
          />
          <div class="medicaid-eligibility-field-feedback">
            <error
              v-if="nationalIdentifierError"
              :message="nationalIdentifierError"
              variant="no-border"
            />
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <span class="font-weight-normal text-gray font-14">
          Medicaid ID:
        </span>

        <p v-if="!isEditing && medicalIdentifier" class="font-16 mb-0 mt-1">
          <strong>{{ medicalIdentifier }}</strong>
        </p>

        <div
          v-else
          :class="{
            'medicaid-eligibility-field-invalid': !!medicalIdentifierError,
          }"
        >
          <custom-input
            v-model="medicalIdentifierDraft"
            class="mt-1"
            maxlength="50"
            placeholder="Ingrese Medicaid ID"
            @input="onMedicalIdentifierInput"
          />
          <div class="medicaid-eligibility-field-feedback">
            <error
              v-if="medicalIdentifierError"
              :message="medicalIdentifierError"
              variant="no-border"
            />
          </div>
        </div>
      </div>

      <div class="col-md-2 col-12 pt-4" style="padding-top: 1.7rem !important;">
        <action-button
          class="ml-auto"
          :i="isEditing ? 'fas fa-save' : 'fas fa-edit'"
          :variant="isEditing ? 'success' : 'outline-success'"
          @click="onToggleEdit"
        />
      </div>
    </div>

    <!-- SECCIÓN DE ELEGIBILIDAD-->
    <!-- BOTÓN INICIAL -->
    <div v-if="!isAdding && !hasEligibilities" class="col-12">
      <div class="bg-secondary-3 p-3 mt-3 rounded">
        <div class="upload-file-button__container">
          <div class="panel-row-without-selector p-4">
            <div class="col-12 text-center">
              <button
                class="btn bg-light-white col-12 col-md-4"
                :disabled="!hasNationalIdentifier"
                @click="isAdding = true"
              >
                <em class="fas fa-file-plus pr-4 text-green" />
                <span class="text-button-add-document">
                  Presione aquí para añadir Elegibilidad Medicaid
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- BOTÓN UNA VEZ YA HAY REGISTROS -->
    <div
      v-if="!isAdding && hasEligibilities"
      class="col-12 d-flex justify-content-end"
    >
      <action-button
        :disabled="!hasNationalIdentifier"
        i="fas fa-file-plus"
        variant="success"
        @click="isAdding = true"
      >
        Añadir
      </action-button>
    </div>

    <div v-else-if="isAdding" class="col-12">
      <div class="bg-secondary-3 p-3 mt-3 rounded">
        <div class="upload-file-button__container">
          <div class="panel-row-without-selector p-4">
            <!-- FORMULARIO -->
            <div class="col-12">
              <validation-observer
                ref="eligibilityObserver"
                v-slot="{ validate }"
              >
                <div class="row">
                  <div class="col-12 mb-3 d-flex align-items-center">
                    <div class="eligibility-icon-wrapper mr-2">
                      <i class="fas fa-clipboard-user" />
                    </div>
                    <span class="eligibility-title-text">
                      Elegibilidad de Medicaid
                    </span>
                  </div>

                  <div class="col-md-3 ">
                    <validation-provider v-slot="{ errors }" rules="required">
                      <custom-date-picker
                        v-model="eligibility.startDate"
                        class="custom-date-picker__extended"
                        custom-error-msg="Favor seleccionar Fecha Inicio."
                        :error="errors[0]"
                        label="Fecha Inicio:"
                        placeholder="DD/MM/YYYY"
                        @input="onStartDateChange"
                      />
                    </validation-provider>
                  </div>

                  <div class="col-md-3">
                    <validation-provider v-slot="{ errors }" rules="required">
                      <custom-date-picker
                        v-model="eligibility.endDate"
                        class="custom-date-picker__extended"
                        custom-error-msg="Favor seleccionar Fecha Fin."
                        :error="errors[0]"
                        label="Fecha Fin:"
                        :min-date="eligibility.startDate"
                        placeholder="DD/MM/YYYY"
                      />
                    </validation-provider>
                  </div>

                  <div class="col-md-6" />

                  <div
                    :class="[
                      'mt-3 mt-md-0',
                      isOtherDocument ? 'col-md-3  col-12' : 'col-md-6  col-12',
                    ]"
                  >
                    <validation-provider
                      v-slot="{ errors }"
                      name="Tipo de Documento"
                      rules="required"
                    >
                      <custom-drop-down
                        v-model="eligibility.documentType"
                        class="mt-1"
                        custom-error-msg="Favor seleccionar tipo de documento."
                        :error="errors[0]"
                        fieldkey="id"
                        fieldname="name"
                        label="Tipo de Documento:"
                        :options="documentTypes"
                        placeholder="Seleccione"
                      />
                    </validation-provider>
                  </div>

                  <div
                    v-if="isOtherDocument"
                    class="col-md-3  col-12 mt-3 mt-md-0"
                  >
                    <validation-provider
                      v-slot="{ errors }"
                      name="Nombre del Documento"
                      rules="required"
                    >
                      <custom-input
                        v-model="eligibility.otherName"
                        class="mt-1"
                        custom-error-msg="Favor ingresar el nombre del documento"
                        :error="errors[0]"
                        label="Nombre del Documento:"
                        placeholder="Ingrese el nombre del documento"
                      />
                    </validation-provider>
                  </div>

                  <!-- Archivo -->
                  <div class="col-md-6 col-12 mt-3 mt-md-0">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Archivo"
                      rules="required"
                    >
                      <label class="text-muted">Archivo:</label>
                      <custom-file-input
                        ref="documentInform"
                        v-model="eligibility.document"
                        accept="pdf"
                        :attachment-id="eligibility.documentId"
                        class="mt-1"
                        custom-error-msg="Favor seleccionar un archivo."
                        display-style="normal"
                        :error="errors[0]"
                        @input="saveSignedDocument($event)"
                      />
                    </validation-provider>
                  </div>

                  <div class="col-12 text-right mt-4">
                    <action-button
                      class="mr-2"
                      i="fas fa-times-circle"
                      variant="outline-danger"
                      @click="onCancel"
                    >
                      Cancelar
                    </action-button>

                    <action-button
                      i="fas fa-flag"
                      variant="success"
                      @click="() => onAddEligibility(validate)"
                    >
                      Someter
                    </action-button>
                  </div>
                </div>
              </validation-observer>
            </div>
            <!-- FORMULARIO -->
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasEligibilities" class="col-12">
      <custom-separator class="my-4" text="Elegibilidad" />
    </div>
    <div v-if="hasEligibilities" class="col-12">
      <medicaid-row-result
        v-for="item in eligibilities"
        :key="item.id"
        is-from-company
        :model="item"
        @refresh="onSearchHandler"
      />
    </div>

    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import CustomSeparator from 'custom-separator';
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import CustomDatePicker from 'custom-date-picker';
import Error from '@/components/common/Error';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

import {
  saveNationalIdentifier,
  saveMedicaidEligibility,
} from '@/services/provider/provider.api.js';

import { get as getMedicaid } from '@/services/medicaid/medicaid-certification.api';
import objectToFormData from 'object-to-formdata';
import Pagination from 'pagination';
import MedicaidRowResult from '@/views/medicaid/certification/components/ResultRow.vue';

export default {
  name: 'MedicaidEligibility',
  components: {
    ActionButton,
    CustomInput,
    CustomSeparator,
    SectionTitle,
    CustomDropDown,
    CustomFileInput,
    CustomDatePicker,
    Error,
    MedicaidRowResult,
    Pagination,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: String,
      default: '',
    },
    medicalIdentifier: {
      type: String,
      default: '',
    },
    providerId: {
      type: Number,
      default: () => 0,
    },
  },
  data() {
    return {
      nationalIdentifierDraft: '',
      medicalIdentifierDraft: '',
      nationalIdentifierError: '',
      medicalIdentifierError: '',
      isEditing: true,
      isAdding: false,
      eligibility: {
        startDate: null,
        endDate: null,
        documentType: null,
        document: null,
        documentId: null,
        otherName: null,
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 1,
        count: 0,
        pageSize: 5,
      },
      documentTypes: [{ id: 1, name: 'Otro' }],
      eligibilities: [],
    };
  },
  computed: {
    nationalIdentifier: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    today() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return now;
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    isOtherDocument() {
      return this.eligibility.documentType?.name === 'Otro';
    },
    hasNationalIdentifier() {
      return (
        !this.isEditing &&
        this.nationalIdentifier &&
        this.nationalIdentifier.trim() !== '' &&
        this.medicalIdentifier &&
        this.medicalIdentifier.trim() !== ''
      );
    },
    hasEligibilities() {
      return this.eligibilities && this.eligibilities.length > 0;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.nationalIdentifierDraft = newVal || '';
        this.syncEditStateFromProps();
      },
    },
    medicalIdentifier: {
      immediate: true,
      handler(newVal) {
        this.medicalIdentifierDraft = newVal || '';
        this.syncEditStateFromProps();
      },
    },
  },
  async mounted() {
    if (this.providerId) {
      await this.onSearchHandler();
    }
  },
  methods: {
    async onToggleEdit() {
      if (this.isEditing) {
        const draft = (this.nationalIdentifierDraft || '').trim();
        const medicalDraft = (this.medicalIdentifierDraft || '').trim();

        this.nationalIdentifierError = '';
        this.medicalIdentifierError = '';

        if (!draft || !medicalDraft) {
          if (!draft) {
            this.nationalIdentifierError =
              'El campo Número de Identificador Nacional es requerido.';
          }
          if (!medicalDraft) {
            this.medicalIdentifierError = 'El campo Medicaid ID es requerido.';
          }
          this.ShowToast('', 'Debe completar los campos requeridos.', 'error');
          return;
        }
        try {
          await saveNationalIdentifier(this.providerId, draft, medicalDraft);

          this.nationalIdentifier = draft;
          this.$emit('update:medicalIdentifier', medicalDraft);
          this.isEditing = false;
          this.nationalIdentifierError = '';
          this.medicalIdentifierError = '';

          this.ShowToast(
            'Proceso Exitoso',
            'Identificadores guardados correctamente.',
            'success'
          );
        } catch (err) {
          const message =
            err?.response?.data?.message ||
            'Ocurrió un error guardando los identificadores.';
          this.ShowToast('Error', message, 'error');
        }
      } else {
        this.nationalIdentifierDraft = this.nationalIdentifier;
        this.medicalIdentifierDraft = this.medicalIdentifier;
        this.nationalIdentifierError = '';
        this.medicalIdentifierError = '';
        this.isEditing = true;
      }
    },
    onNationalIdentifierInput(value) {
      if ((value || '').trim()) {
        this.nationalIdentifierError = '';
      }
    },
    onMedicalIdentifierInput(value) {
      if ((value || '').trim()) {
        this.medicalIdentifierError = '';
      }
    },
    syncEditStateFromProps() {
      const hasNational = !!(
        this.nationalIdentifier && this.nationalIdentifier.trim() !== ''
      );
      const hasMedical = !!(
        this.medicalIdentifier && this.medicalIdentifier.trim() !== ''
      );
      this.isEditing = !(hasNational && hasMedical);
    },

    onCancel() {
      this.eligibility = {
        startDate: null,
        endDate: null,
        documentType: null,
        document: null,
        documentId: null,
        otherName: null,
      };
      this.isAdding = false;
      this.$refs.eligibilityObserver && this.$refs.eligibilityObserver.reset();
    },
    onStartDateChange() {
      this.eligibility.endDate = null;
    },
    async onSearchHandler() {
      const payload = {
        companyId: this.providerId,
        currentPage: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };

      const { data: response = { data: [] } } = await getMedicaid(payload);

      this.eligibilities = response.data;

      this.pagination = {
        ...this.pagination,
        totalItemCount: response.totalItemCount,
        pageCount: response.pageCount,
        currentPage: response.currentPage,
        count: response.count,
      };
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      await this.onSearchHandler();
    },
    async onAddEligibility(validateFn) {
      const isValid = await validateFn();
      if (!isValid) {
        this.ShowToast(
          'Validación',
          'Debe completar correctamente todos los campos de Elegibilidad Medicaid.',
          'error'
        );
        return;
      }

      if (!this.isOtherDocument) {
        this.eligibility.otherName = null;
      }

      if (!this.eligibility.document) {
        this.ShowToast(
          'Validación',
          'Debe adjuntar un archivo de elegibilidad Medicaid.',
          'warning'
        );
        return;
      }

      try {
        const payload = objectToFormData(
          {
            startDate: this.eligibility.startDate,
            endDate: this.eligibility.endDate,
            otherName: this.eligibility.otherName,
            file: this.eligibility.document,
          },
          {
            indices: true,
            nullsAsUndefineds: true,
          }
        );

        await saveMedicaidEligibility(this.providerId, payload);

        this.ShowToast(
          'Proceso Exitoso',
          'Elegibilidad de Medicaid añadida correctamente.',
          'success'
        );
        await this.onSearchHandler();
        this.onCancel();
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          'Ocurrió un error guardando la elegibilidad de Medicaid.';
        this.ShowToast('Error', message, 'error');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.custom-date-picker__extended ::v-deep {
  .width-container {
    width: 100% !important;
  }
}
.eligibility-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  background-color: #ffff;
}
.eligibility-icon-wrapper i {
  font-size: 14px;
  color: #ff6f5a;
}

.eligibility-title-text {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
.medicaid-eligibility-field-invalid ::v-deep .form-control {
  border-color: #ff0018 !important;
}
.medicaid-eligibility-field-feedback {
  min-height: 2.75rem;
}
::v-deep .custom-datetime-picker-container i {
  font-weight: 500 !important;
}
</style>
